(ns admin.components.composite.resource-manager
  (:require
   [reagent.core :as reagent]
   [re-frame.core :as re-frame]

   [kyber.components.base.button :as button]
   [kyber.components.base.text :as text]
   [kyber.components.composite.resource-icon-preview
    :as resource-icon-preview]
   [kyber.styles.core :refer [gs styles->classes]]
   [kyber.lib.kyber-db :as kyber-db]

   [admin.utils :as au]
   [admin.components.composite.learning-objective-pill :as lo-pill]
   [admin.components.composite.resource-selector :as resource-selector]
   [admin.components.composite.resource-uploader :as resource-uploader]
   [admin.components.forms.resource-metadata :as resource-metadata-form]))

(def classes
  (styles->classes
   {:selection-container
    {:padding-bottom (gs [:spacing :p12])
     :margin-bottom (gs [:spacing :p12])
     :border-bottom (str "1px solid " (gs [:colors :gray-scale-100]))}

    :cta-container
    {:display :flex
     :align-items :center
     :margin-top (gs [:spacing :p20])
     :margin-bottom (gs [:spacing :p20])}

    :align-center
    {:display :flex
     :align-items :center}

    :separator
    {:margin-left (gs [:spacing :p20])
     :margin-right (gs [:spacing :p20])}

    :cancel-link-button
    {:margin [[(gs [:spacing :p20])
               :auto
               (gs [:spacing :p40])
               :auto]]}

    :resource-preview-container
    {:margin-bottom (gs [:spacing :p8])
     :padding-bottom (gs [:spacing :p8])
     :border-bottom (str "1px solid " (gs [:colors :gray-scale-100]))
     :position :relative
     :display :flex
     :align-items :flex-start}}))

(defn- render-add-view
  [_]
  (let [selection-method (reagent/atom nil)]
    (fn [{:keys [adding-new-resource
                on-resource-uploaded
                on-resource-selected
                on-cancel
                on-added
                available-resources
                activity-uuid
                curriculum-uuid
                learning-objectives]}]
      (let [cancel-fn #(do
                         (.preventDefault %)
                         (reset! selection-method nil)
                         (on-cancel))]
        [:div {:class (:selection-container classes)}
         (condp = @selection-method
           :upload-resource
           [:div
            [resource-uploader/render
             {:on-success #(do
                             (on-resource-uploaded %1 %2)
                             (on-added %1))
              :activity-uuid activity-uuid
              :curriculum-uuid curriculum-uuid}]
            [button/button-link
             {:on-click cancel-fn
              :class (:cancel-link-button classes)}
             (if @adding-new-resource
               "Cancel file upload"
               "Close")]]

           :select-resource
           [:div
            [resource-selector/render
             {:on-cancel cancel-fn
              :on-select #(do
                            (on-resource-selected %)
                            (reset! selection-method nil)
                            (on-added %))
              :resources available-resources
              :learning-objectives learning-objectives
              :activity-uuid activity-uuid}]
            [button/button-link
             {:on-click cancel-fn
              :class (:cancel-link-button classes)}
             "Cancel and close resource library"]]

           nil
           [:div {:class (:cta-container classes)}
            [button/secondary-button
             {:on-click #(do
                           (.preventDefault %)
                           (reset! adding-new-resource true)
                           (reset! selection-method :upload-resource))
              :style {:margin-right (gs [:spacing :p20])}}
             "Upload New File"]

            [button/secondary-button
             {:on-click #(do
                           (.preventDefault %)
                           (reset! adding-new-resource true)
                           (reset! selection-method :select-resource))}
             "Choose From Resource Library"]])]))))

;; TODO Add back in edit button with next release
(defn- render-resource-preview
  [{:keys [learning-objective
           resource
           editing-resource
           is-selected-inline-media
           inline-media-toggle-disabled
           toggle-inline-media-selection]}]
  (let [{resource-uuid :resource/uuid
         resource-name :resource/name}
        resource]
    [:li {:class (:resource-preview-container classes)}
     [:div {:style {:width "100%"}}
      [:div {:class (:align-center classes)}
       (when (and (some? toggle-inline-media-selection)
                  (au/supported-highlighted-resource? resource))
         [button/icon-button
          {:disabled inline-media-toggle-disabled
           :style {:flex-shrink 0}
           :on-click #(toggle-inline-media-selection
                       (if (true? is-selected-inline-media)
                         nil
                         resource-uuid))}
          (if (true? is-selected-inline-media)
            :star-filled
            :star)])
       [resource-icon-preview/render resource {:small true}]
       [:div {:style {:min-width "0%"
                      :flex-grow 1}}
        [text/caption-30 :p {:style {:word-break :break-all
                                     :margin-right (gs [:spacing :p8])}}
         resource-name]]
       [:div
        [lo-pill/render {:label (:learning-objective/label learning-objective)
                         :max-width 150}]]]

      [:div {:class (:align-center classes)
             :style {:margin-left (gs [:spacing :p24])}}
       [button/button-link
        {:on-click
         #(re-frame/dispatch
           [:resource-library/open-resource-viewer resource])
         :style {:margin (gs [:spacing :p4])}}
        "Preview"]
       [button/button-link
        {:on-click #(do
                      (.preventDefault %)
                      (reset! editing-resource resource-uuid))
         :style {:margin (gs [:spacing :p4])}}
        "Edit"]]]]))

(defn- render-resource-list
  [{:keys [selected-resources
           editing-resource
           toggle-inline-media-selection
           selected-inline-media-uuid
           is-updating]}]
  (let [learning-objectives
        @(re-frame/subscribe
          [::kyber-db/get-records
           {:model :learning-objective
            :idents (mapv #(get-in % [:resource/learning-objective
                                      :learning-objective/uuid]) selected-resources)}])]
    [:div {:style {:width "100%"}}
     [:ul
      (for [{resource-uuid :resource/uuid
             :as resource} selected-resources
            :let [lo-uuid
                  (get-in resource [:resource/learning-objective
                                    :learning-objective/uuid])
                  learning-objective (->> learning-objectives
                                          (filterv #(= lo-uuid (:learning-objective/uuid %)))
                                          first)]]
        ^{:key resource-uuid}
        [render-resource-preview
         (cond-> {:editing-resource editing-resource
                  :learning-objective learning-objective
                  :resource resource}
           (some? toggle-inline-media-selection)
           (merge {:is-selected-inline-media
                   (= resource-uuid selected-inline-media-uuid)

                   :toggle-inline-media-selection
                   toggle-inline-media-selection

                   :inline-media-toggle-disabled
                   (if (some? is-updating)
                     is-updating
                     false)}))])]]))

;; TODO Move to util lib
(defn- available-resources [all-resources selected-resources]
  (let [selected-resource-uuids
        (set (map :resource/uuid selected-resources))]
    (remove #(selected-resource-uuids (:resource/uuid %))
            all-resources)))

(defn render
  [_]
  (let [adding-new-resource (reagent/atom false)
        editing-resource (reagent/atom nil)]
    (fn [{:keys [learning-objectives
                 activity-uuid
                 curriculum-uuid
                 on-resource-removed
                 selected-resources
                 all-resources]
         :as props}]
      (let [available-resources
            (available-resources all-resources selected-resources)]
        [:div
         (when (nil? @editing-resource)
           [render-add-view
            (assoc props
                   :learning-objectives learning-objectives
                   :adding-new-resource adding-new-resource
                   :on-cancel #(reset! adding-new-resource false)
                   :on-added #(reset! adding-new-resource false)
                   :available-resources available-resources)])

         (when (some? @editing-resource)
           [:div
            [resource-metadata-form/render
             (cond-> {:on-cancel #(reset! editing-resource nil)}
               (some? learning-objectives)
               (assoc :learning-objectives learning-objectives)

               (some? activity-uuid)
               (assoc :activity-uuid activity-uuid)

               (some? curriculum-uuid)
               (assoc :curriculum-uuid curriculum-uuid)

               (some? on-resource-removed)
               (assoc :on-resource-removed #(do
                                              (reset! editing-resource nil)
                                              (on-resource-removed %))))
             (get selected-resources @editing-resource)]])

         (when (and (false? @adding-new-resource)
                    (nil? @editing-resource))
           (if (empty? selected-resources)
             [text/caption-30 :p
              {:style {:text-align :center
                       :margin (gs [:spacing :p20])
                       :color (gs [:colors :gray-scale-400])}}
              "No resources added"]
             [render-resource-list
              (assoc props
                     :selected-resources (vals selected-resources)
                     :editing-resource editing-resource)]))]))))
